/**
 * Axios API client with interceptors
 * Handles auth tokens, refresh, and error transformation
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_TIMEOUT } from '../constants';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types';

import { API_BASE_URL } from '../config/env';
const BASE_URL = API_BASE_URL;

import {
  AUTH_EVENTS,
  ensureValidAccessToken,
  isAccessTokenExpired,
  refreshAccessToken,
  shouldAttemptTokenRefresh,
  type BearerTokenRejectedPayload,
} from './auth/tokenRefresh';
import { eventBus } from '../platform/eventBus';

/**
 * Per-request identity overrides.
 *
 * The affiliate portal signs in against `/api/v1/Affiliate/AffiliateValidateLogin`
 * and holds a bearer that has nothing to do with the member session. Attaching
 * the member token there would authorise the wrong account, and letting a 401
 * fall into the member refresh would clear a signed-in member's session while an
 * affiliate was browsing.
 *
 * - `bearerToken` — use this bearer verbatim, and opt out of refresh and retry.
 * - `anonymous` — send no Authorization at all. Needed for the calls that
 *   establish a second identity: a member bearer on them is meaningless and
 *   would ask the API to authorise the wrong account.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    bearerToken?: string;
    anonymous?: boolean;
    /**
     * Let a service normalize an endpoint-specific contradictory success
     * envelope (`success: false` with an embedded 2xx status code).
     */
    normalizeResponseBody?: boolean;
  }
}

/** Request config augmented with one-shot retry flags. */
interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

type QueuedRequest = {
  resolve: (value: AxiosResponse) => void;
  reject: (reason?: unknown) => void;
  config: RetriableRequestConfig;
};

type FailedRequest = {
  code?: string;
  message?: string;
  response?: {
    status: number;
    data?: unknown;
  };
  config?: RetriableRequestConfig;
};

let isRefreshingAfter401 = false;
let refreshWaitQueue: QueuedRequest[] = [];

const flushRefreshQueue = (error: unknown | null, token: string | null): void => {
  refreshWaitQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(toUserFacingError(error));
      return;
    }
    if (!token) {
      reject(new Error('Session expired'));
      return;
    }
    config._retried = true;
    config.headers.Authorization = `Bearer ${token}`;
    apiClient(config).then(resolve, reject);
  });
  refreshWaitQueue = [];
};

const getBearerTokenFromConfig = (
  config?: InternalAxiosRequestConfig,
): string | null => {
  if (!config?.headers) {
    return null;
  }
  const headers = config.headers as {
    Authorization?: unknown;
    authorization?: unknown;
    get?: (name: string) => unknown;
  };
  const value =
    typeof headers.get === 'function'
      ? (headers.get('Authorization') ?? headers.get('authorization'))
      : (headers.Authorization ?? headers.authorization);
  if (typeof value !== 'string' || !value.startsWith('Bearer ')) {
    return null;
  }
  const token = value.slice(7).trim();
  return token || null;
};

const toUserFacingError = (error: unknown): Error => {
  const message = getApiErrorMessage(error as ApiError);
  if (error instanceof Error) {
    error.message = message;
    return error;
  }
  const wrapped = new Error(message);
  if (error && typeof error === 'object') {
    Object.assign(wrapped, error, { message });
  }
  return wrapped;
};

const isAuthStatus = (status: number | undefined): boolean =>
  status === 401;

/**
 * A bearer that cannot be used again, matching `isAuthSessionFailure`. Wider
 * than `isAuthStatus` because that one gates the member refresh, where a 403 is
 * a permission answer rather than a stale token.
 */
const isTerminalAuthStatus = (status: number | undefined): boolean =>
  status === 401 || status === 403;

/**
 * Recover a failed call when the access token is stale:
 * 401, or 5xx/timeout after idle with an expired JWT.
 * Concurrent callers share one refresh and retry afterward.
 */
const recoverOrReject = async (error: FailedRequest): Promise<AxiosResponse> => {
  const originalRequest = error.config;
  const status = error.response?.status;
  const canAttemptRefresh =
    Boolean(originalRequest) &&
    !originalRequest?._retried &&
    !originalRequest?.bearerToken &&
    !originalRequest?.anonymous &&
    shouldAttemptTokenRefresh(originalRequest?.url);

  const bearerExpired = isAccessTokenExpired(getBearerTokenFromConfig(originalRequest));
  const shouldRefreshSession =
    canAttemptRefresh &&
    (isAuthStatus(status) ||
      ((status == null || status >= 500) && bearerExpired));

  if (shouldRefreshSession && originalRequest) {
    if (isRefreshingAfter401) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        refreshWaitQueue.push({
          resolve,
          reject,
          config: originalRequest,
        });
      });
    }

    originalRequest._retried = true;
    isRefreshingAfter401 = true;

    try {
      const token = await refreshAccessToken();
      flushRefreshQueue(null, token);
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      flushRefreshQueue(refreshError, null);
      return Promise.reject(toUserFacingError(refreshError));
    } finally {
      isRefreshingAfter401 = false;
    }
  }

  // A caller-supplied bearer is deliberately excluded from refresh above, so a
  // 401 on it is terminal: that identity's session is over. Announce the exact
  // token and let its owner clear itself — the member session is untouched.
  if (originalRequest?.bearerToken && isTerminalAuthStatus(status)) {
    const payload: BearerTokenRejectedPayload = {
      bearerToken: originalRequest.bearerToken,
    };
    eventBus.emit(AUTH_EVENTS.BEARER_TOKEN_REJECTED, payload);
  }

  return Promise.reject(toUserFacingError(error));
};

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use(
  async config => {
    // Let the runtime set multipart boundary — a hard-coded Content-Type
    // without boundary breaks FormData uploads (comments, posts, etc.).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
    }

    // A caller-supplied bearer replaces the member session for this request.
    if (config.bearerToken) {
      config.headers.Authorization = `Bearer ${config.bearerToken}`;
      return config;
    }

    if (config.anonymous || !shouldAttemptTokenRefresh(config.url)) {
      return config;
    }

    try {
      const token = await ensureValidAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      return Promise.reject(toUserFacingError(error));
    }

    return config;
  },
  error => Promise.reject(toUserFacingError(error)),
);

apiClient.interceptors.response.use(
  response => {
    const data = response.data as { success?: boolean; statusCode?: number; message?: string };
    const serviceNormalizesSuccessfulBody =
      response.config.normalizeResponseBody === true &&
      typeof data?.statusCode === 'number' &&
      data.statusCode >= 200 &&
      data.statusCode < 300;
    if (
      data &&
      data.success === false &&
      !serviceNormalizesSuccessfulBody
    ) {
      return recoverOrReject({
        response: {
          status: data.statusCode || response.status || 400,
          data,
        },
        message: data.message || 'API Error',
        config: response.config as RetriableRequestConfig,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetriableRequestConfig & { _networkRetries?: number };
    
    // Automatically retry transient network errors (no response, timeout, etc.) for GET requests
    if (config && !error.response && error.isAxiosError && config.method?.toLowerCase() === 'get') {
      config._networkRetries = config._networkRetries || 0;
      if (config._networkRetries < 2) {
        config._networkRetries += 1;
        await new Promise(resolve => setTimeout(() => resolve(undefined), 1000));
        return apiClient(config);
      }
    }
    
    return recoverOrReject(error as FailedRequest);
  },
);

export default apiClient;
